import type { DocumentNode } from 'graphql';
import { remark } from 'remark';
import stripMarkdown from 'strip-markdown';
import {
  BoltIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
  CircleStackIcon,
  CubeIcon,
  DocumentIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FolderIcon,
  MicrophoneIcon,
  PaintBrushIcon,
  PhoneIcon,
  QueueListIcon,
  SparklesIcon,
  TableCellsIcon,
  TicketIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

import {
  Action,
  CaraWorkspaceSortField,
  ClientSortField,
  CustomPresetState,
  GetCallsDocument,
  GetCaraWorkspaceArtifactsDocument,
  GetCustomFormsDocument,
  GetCustomPresetsDocument,
  GetEmailThreadsDocument,
  GetExcelSpreadsheetTemplatesDocument,
  GetHtmlDocumentTemplatesDocument,
  GetKnowledgeBaseDocumentsDocument,
  GetKnowledgeBasesDocument,
  GetServiceRequestsDocument,
  GetTriggerDefinitionsDocument,
  GetTriggerEventsDocument,
  GetVoiceAgentsDocument,
  GetWordDocumentTemplatesDocument,
  Resource,
  SearchCaraWorkspacesDocument,
  SearchClientsDocument,
  SearchDocumentationDocument,
  SortDirection,
  TriggerDefinitionStatus,
  type GetCallsQuery,
  type GetCaraWorkspaceArtifactsQuery,
  type GetCustomFormsQuery,
  type GetCustomPresetsQuery,
  type GetEmailThreadsQuery,
  type GetExcelSpreadsheetTemplatesQuery,
  type GetHtmlDocumentTemplatesQuery,
  type GetKnowledgeBaseDocumentsQuery,
  type GetKnowledgeBasesQuery,
  type GetServiceRequestsQuery,
  type GetTriggerDefinitionsQuery,
  type GetTriggerEventsQuery,
  type GetVoiceAgentsQuery,
  type GetWordDocumentTemplatesQuery,
  type SearchCaraWorkspacesQuery,
  type SearchClientsQuery,
  type SearchDocumentationQuery
} from '~/api/graphql';
import { FEATURE_FLAGS } from '~/components/feature-flags';
import { getKnowledgeBaseDocumentDisplayTitle } from '~/components/knowledge/document-display-name';
import { defaultDocumentVersion } from '~/components/documents/document-versions';
import {
  documentPreviewPath,
  documentsListPath,
  isDocumentArtifact
} from '~/components/documents/documents.utils';
import { EVENT_TYPE_LABELS, eventSummary } from '~/pages/workflows/trigger-utils';
import { byUpdatedAtDesc, matchesTerms, relativeDate } from './match';
import {
  SECTION_RESULT_LIMIT,
  type SearchAccess,
  type SearchContext,
  type SearchResult,
  type SearchSection
} from './types';

/**
 * Runs a list query for the palette, reading the cache before the network.
 *
 * Overrides the app-wide `network-only` default. Route loaders keep that
 * default, so ordinary navigation refreshes these same lists and the palette
 * reads back what they wrote; a search then costs a request only for the
 * sections whose variables carry the query string. The tradeoff is that a
 * section whose query no page shares stays as fresh as its first fetch and no
 * fresher.
 */
const fetchList = async <Q>(
  { client }: SearchContext,
  document: DocumentNode,
  variables?: Record<string, unknown>,
  fetchPolicy: 'cache-first' | 'no-cache' = 'cache-first'
) => {
  const { data } = await client.query<Q>({ query: document, variables, fetchPolicy });
  return data;
};

/**
 * These filters carry the query string, so every search is a cache miss anyway.
 * Bypassing costs nothing and keeps the palette independent of how
 * Query.clients and Query.searchCaraWorkspaces are keyed.
 */
const SHARED_WITH_A_LIST_PAGE = 'no-cache' as const;

/** A preset that is still generating, or whose generation failed, cannot be run. */
const isRunnablePreset = (state: CustomPresetState) =>
  state === CustomPresetState.Ready || state === CustomPresetState.NeedsReview;

export const workspacesSection: SearchSection = {
  key: 'workspaces',
  title: 'Workspaces',
  icon: SparklesIcon,
  sectionClass: 'content',
  // The only section with a real server-side text search: searchCaraWorkspaces
  // matches title, client name and keywords behind a trigram index, so the
  // query goes over the wire rather than filtering a full list in the browser.
  search: async (context) => {
    const data = await fetchList<SearchCaraWorkspacesQuery>(
      context,
      SearchCaraWorkspacesDocument,
      {
        first: SECTION_RESULT_LIMIT,
        filter: { search: context.query },
        sort: { field: CaraWorkspaceSortField.UpdatedAt, direction: SortDirection.Desc }
      },
      SHARED_WITH_A_LIST_PAGE
    );

    return data.searchCaraWorkspaces.edges.map(({ node }) => ({
      id: node.id,
      label: node.title || 'Untitled workspace',
      description: node.metadata.clientName || undefined,
      date: relativeDate(node.updatedAt),
      primary: { label: 'Open', target: { kind: 'route', to: `/app/workspaces/${node.id}` } }
    }));
  }
};

export const clientsSection: SearchSection = {
  key: 'clients',
  title: 'Clients',
  icon: UserGroupIcon,
  sectionClass: 'content',
  hidden: (access) => !access.flags.has(FEATURE_FLAGS.SHOW_CLIENTS_TAB),
  // Server-side too: ClientFilterInput.nameQuery is a case-insensitive
  // substring match, the same one the workspace-association picker uses.
  search: async (context) => {
    const data = await fetchList<SearchClientsQuery>(
      context,
      SearchClientsDocument,
      {
        first: SECTION_RESULT_LIMIT,
        filter: { nameQuery: context.query },
        sort: { field: ClientSortField.UpdatedAt, direction: SortDirection.Desc }
      },
      SHARED_WITH_A_LIST_PAGE
    );

    return data.clients.edges.map(({ node }) => ({
      id: node.id,
      label: node.name,
      description: [node.email, node.phone].filter(Boolean).join(' · ') || undefined,
      date: relativeDate(node.updatedAt),
      primary: { label: 'Open', target: { kind: 'route', to: `/app/clients/${node.id}` } }
    }));
  }
};

const presetResults = (
  { terms }: SearchContext,
  presets: GetCustomPresetsQuery['customPresets'],
  owned: boolean
): SearchResult[] =>
  presets
    .filter((preset) => preset.isOwner === owned && isRunnablePreset(preset.state))
    .filter((preset) =>
      matchesTerms(terms, preset.title, preset.description, preset.createdBy?.name)
    )
    .sort(byUpdatedAtDesc)
    .map((preset) => ({
      id: preset.id,
      label: preset.title,
      description: preset.description ?? preset.createdBy?.name ?? undefined,
      date: relativeDate(preset.updatedAt),
      primary: { label: 'Run', target: { kind: 'route', to: `/app/presets/${preset.id}/run` } },
      // Only the owner gets Edit -- the edit route rejects everyone else, so
      // offering it on a shared preset would be a dead key.
      ...(owned
        ? {
            secondary: {
              label: 'Edit',
              target: { kind: 'route' as const, to: `/app/presets/${preset.id}/edit` }
            }
          }
        : {})
    }));

export const customPresetsSection: SearchSection = {
  key: 'custom-presets',
  title: 'Custom presets',
  icon: CubeIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetCustomPresetsQuery>(context, GetCustomPresetsDocument);
    return presetResults(context, data.customPresets, true);
  }
};

export const sharedPresetsSection: SearchSection = {
  key: 'shared-presets',
  title: 'Shared presets',
  icon: CubeIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetCustomPresetsQuery>(context, GetCustomPresetsDocument);
    return presetResults(context, data.customPresets, false);
  }
};

/**
 * A style is an HTML document template with no body; a template is one with a
 * body. The two tabs of the library split the same list this way, so the
 * palette does too rather than inventing a third rule.
 */
const isStyle = (template: { bodyTemplate?: string | null }) =>
  !template.bodyTemplate || template.bodyTemplate.trim() === '';

export const stylesSection: SearchSection = {
  key: 'styles',
  title: 'Styles',
  icon: PaintBrushIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetHtmlDocumentTemplatesQuery>(
      context,
      GetHtmlDocumentTemplatesDocument
    );

    return data.htmlDocumentTemplates
      .filter(isStyle)
      .filter((style) => matchesTerms(context.terms, style.title, style.description))
      .sort(byUpdatedAtDesc)
      .map((style) => ({
        id: style.id,
        label: style.title,
        description: style.description ?? undefined,
        date: relativeDate(style.updatedAt),
        primary: {
          label: 'Open',
          target: { kind: 'route', to: `/app/library/styles/${style.id}` }
        }
      }));
  }
};

/**
 * A template row, whatever list it came from. `libraryTab` is the library tab
 * that owns it, which is also the segment its edit route sits under.
 */
type TemplateRow = {
  id: string;
  title: string;
  description?: string | null;
  updatedAt?: Date | string | null;
  createdAt?: Date | string | null;
  templateFile?: { name: string } | null;
};

const templateResults = (
  { terms }: SearchContext,
  templates: TemplateRow[],
  libraryTab: 'templates' | 'word-templates' | 'excel-templates'
): SearchResult[] =>
  templates
    .filter((template) =>
      matchesTerms(terms, template.title, template.description, template.templateFile?.name)
    )
    .sort(byUpdatedAtDesc)
    .map((template) => ({
      id: template.id,
      label: template.title,
      description: template.description || template.templateFile?.name || undefined,
      date: relativeDate(template.updatedAt),
      primary: {
        label: 'Open',
        target: { kind: 'route', to: `/app/library/${libraryTab}/${template.id}` }
      }
    }));

export const templatesSection: SearchSection = {
  key: 'templates',
  title: 'Templates',
  icon: BuildingLibraryIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetHtmlDocumentTemplatesQuery>(
      context,
      GetHtmlDocumentTemplatesDocument
    );

    return templateResults(
      context,
      data.htmlDocumentTemplates.filter((template) => !isStyle(template)),
      'templates'
    );
  }
};

export const wordTemplatesSection: SearchSection = {
  key: 'word-templates',
  title: 'Word templates',
  icon: DocumentIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetWordDocumentTemplatesQuery>(
      context,
      GetWordDocumentTemplatesDocument
    );

    return templateResults(context, data.wordDocumentTemplates, 'word-templates');
  }
};

export const excelTemplatesSection: SearchSection = {
  key: 'excel-templates',
  title: 'Excel templates',
  icon: TableCellsIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetExcelSpreadsheetTemplatesQuery>(
      context,
      GetExcelSpreadsheetTemplatesDocument
    );

    return templateResults(context, data.excelSpreadsheetTemplates, 'excel-templates');
  }
};

export const formsSection: SearchSection = {
  key: 'forms',
  title: 'Forms',
  icon: DocumentTextIcon,
  sectionClass: 'catalog',
  search: async (context) => {
    const data = await fetchList<GetCustomFormsQuery>(context, GetCustomFormsDocument);

    return data.customForms
      .filter((form) => !form.archivedAt)
      .filter((form) => matchesTerms(context.terms, form.title, form.templateFile?.name))
      .sort(byUpdatedAtDesc)
      .map((form) => ({
        id: form.id,
        label: form.title,
        description: form.templateFile?.name ?? undefined,
        date: relativeDate(form.updatedAt),
        primary: { label: 'Open', target: { kind: 'route', to: `/app/library/forms/${form.id}` } }
      }));
  }
};

export const knowledgeBasesSection: SearchSection = {
  key: 'knowledge-bases',
  title: 'Knowledge bases',
  icon: CircleStackIcon,
  sectionClass: 'catalog',
  hidden: (access) => !access.hasPermission(Resource.KnowledgeBase, Action.List),
  search: async (context) => {
    const data = await fetchList<GetKnowledgeBasesQuery>(context, GetKnowledgeBasesDocument);

    return data.knowledgeBases
      .filter((base) => matchesTerms(context.terms, base.title, base.ownerAgency?.name))
      .sort(byUpdatedAtDesc)
      .map((base) => ({
        id: base.id,
        label: base.title,
        description: `${base.documents.length} document${base.documents.length === 1 ? '' : 's'}`,
        date: relativeDate(base.updatedAt),
        primary: { label: 'Open', target: { kind: 'route', to: `/app/knowledge/${base.id}` } }
      }));
  }
};

/**
 * The documents inside the knowledge bases, which the bases section does not
 * reach: knowledgeBases returns each base as one row, and a base named
 * "Carrier appetite" says nothing about the guide filed under it.
 */
export const knowledgeDocumentsSection: SearchSection = {
  key: 'knowledge-documents',
  title: 'Knowledge documents',
  icon: DocumentMagnifyingGlassIcon,
  sectionClass: 'content',
  hidden: (access) => !access.hasPermission(Resource.KnowledgeBase, Action.List),
  search: async (context) => {
    const data = await fetchList<GetKnowledgeBaseDocumentsQuery>(
      context,
      GetKnowledgeBaseDocumentsDocument
    );

    return data.knowledgeBases
      .flatMap((base) => base.documents.map((document) => ({ base, document })))
      .filter(({ document }) =>
        matchesTerms(
          context.terms,
          getKnowledgeBaseDocumentDisplayTitle(document),
          ...(document.metadata?.keywords ?? [])
        )
      )
      .sort((a, b) => byUpdatedAtDesc(a.document, b.document))
      .map(({ base, document }) => ({
        id: document.id,
        // The same title the knowledge base page shows, which is an author's
        // display name override ahead of the extracted or uploaded one.
        label: getKnowledgeBaseDocumentDisplayTitle(document) || 'Untitled document',
        description: base.title,
        date: relativeDate(document.updatedAt),
        primary: {
          label: 'Open',
          target: { kind: 'route', to: `/app/knowledge/${base.id}/${document.id}` }
        },
        secondary: {
          label: 'Knowledge base',
          target: { kind: 'route', to: `/app/knowledge/${base.id}` }
        }
      }));
  }
};

/**
 * Both directions in one section: inbound and outbound agents are separate
 * resources to the API but share a settings route, so splitting them would put
 * two groups in the palette that land in the same place.
 */
export const voiceAgentsSection: SearchSection = {
  key: 'voice-agents',
  title: 'Voice agents',
  icon: MicrophoneIcon,
  sectionClass: 'catalog',
  hidden: (access) => !access.hasPermission(Resource.VoiceAgent, Action.List),
  // VoiceAgentsFilterInput narrows by direction only, so the name is matched here.
  search: async (context) => {
    const data = await fetchList<GetVoiceAgentsQuery>(context, GetVoiceAgentsDocument);

    return data.voiceAgents
      .filter((agent) => matchesTerms(context.terms, agent.name))
      .sort(byUpdatedAtDesc)
      .map((agent) => ({
        id: agent.id,
        label: agent.name,
        description: agent.phoneNumber ?? undefined,
        date: relativeDate(agent.updatedAt),
        primary: {
          label: 'Open',
          target: { kind: 'route', to: `/app/settings/voice/${agent.id}` }
        }
      }));
  }
};

export const documentsSection: SearchSection = {
  key: 'documents',
  title: 'Documents',
  icon: FolderIcon,
  sectionClass: 'content',
  search: async (context) => {
    const data = await fetchList<GetCaraWorkspaceArtifactsQuery>(
      context,
      GetCaraWorkspaceArtifactsDocument
    );

    return data.caraWorkspaceArtifacts
      .filter(
        (artifact) =>
          isDocumentArtifact(artifact.type) &&
          matchesTerms(
            context.terms,
            artifact.title,
            artifact.metadata.clientName,
            artifact.metadata.workspaceTitle
          )
      )
      .sort(byUpdatedAtDesc)
      .flatMap((artifact) => {
        // The document route is version-addressed, so an artifact with no
        // versions yet has nowhere to open and is dropped rather than linked
        // to a URL that would 404.
        const version = defaultDocumentVersion(artifact.versions);
        if (!version) {
          return [];
        }

        return [
          {
            id: artifact.id,
            label: artifact.title,
            description:
              artifact.metadata.clientName || artifact.metadata.workspaceTitle || undefined,
            date: relativeDate(artifact.updatedAt),
            primary: {
              label: 'Open',
              target: {
                kind: 'route' as const,
                to: documentPreviewPath(
                  documentsListPath(false),
                  artifact.metadata.workspaceId,
                  artifact.id,
                  version.version
                )
              }
            },
            secondary: {
              label: 'Workspace',
              target: {
                kind: 'route' as const,
                to: `/app/workspaces/${artifact.metadata.workspaceId}`
              }
            }
          }
        ];
      });
  }
};

export const emailsSection: SearchSection = {
  key: 'emails',
  title: 'Emails',
  icon: EnvelopeIcon,
  sectionClass: 'content',
  search: async (context) => {
    const data = await fetchList<GetEmailThreadsQuery>(context, GetEmailThreadsDocument);

    return data.emailThreads
      .filter((thread) =>
        matchesTerms(
          context.terms,
          thread.subject,
          thread.fromName,
          thread.fromEmail,
          thread.account?.account.name
        )
      )
      .sort(byUpdatedAtDesc)
      .map((thread) => ({
        id: thread.id,
        label: thread.subject || '(no subject)',
        description: thread.fromName ?? thread.fromEmail ?? undefined,
        date: relativeDate(thread.updatedAt),
        primary: { label: 'Open', target: { kind: 'route', to: `/app/mailbox/inbox/${thread.id}` } }
      }));
  }
};

export const callsSection: SearchSection = {
  key: 'calls',
  title: 'Calls',
  icon: PhoneIcon,
  sectionClass: 'content',
  // CallFilterInput can only narrow by number, status and sentiment, so the
  // caller-name half of the query has to be matched here.
  search: async (context) => {
    const data = await fetchList<GetCallsQuery>(context, GetCallsDocument, {
      filter: { archived: false }
    });

    return data.calls
      .filter((call) =>
        matchesTerms(context.terms, call.fromName, call.fromNumber, call.account?.account.name)
      )
      .sort((a, b) => new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime())
      .map((call) => ({
        id: call.id,
        label: call.fromName || call.fromNumber || 'Unknown caller',
        description: call.account?.account.name ?? call.fromNumber ?? undefined,
        date: relativeDate(call.startedAt),
        primary: { label: 'Open', target: { kind: 'route', to: `/app/calls/log/${call.id}` } }
      }));
  }
};

export const serviceRequestsSection: SearchSection = {
  key: 'service-requests',
  title: 'Service requests',
  icon: TicketIcon,
  sectionClass: 'content',
  search: async (context) => {
    const data = await fetchList<GetServiceRequestsQuery>(context, GetServiceRequestsDocument);

    return data.serviceRequests
      .filter((request) =>
        matchesTerms(
          context.terms,
          request.clientName,
          request.clientEmail,
          request.details,
          request.account?.account.name
        )
      )
      .sort(byUpdatedAtDesc)
      .map((request) => ({
        id: request.id,
        label: request.clientName || request.clientEmail || 'Service request',
        description: request.details ?? request.account?.account.name ?? undefined,
        date: relativeDate(request.updatedAt),
        primary: { label: 'Open', target: { kind: 'route', to: `/app/servicing/${request.id}` } }
      }));
  }
};

const canListTriggers = (access: SearchAccess) =>
  access.flags.has(FEATURE_FLAGS.SHOW_WORKFLOWS_TAB) &&
  access.hasPermission(Resource.TriggerDefinition, Action.List);

export const triggersSection: SearchSection = {
  key: 'triggers',
  title: 'Triggers',
  icon: BoltIcon,
  sectionClass: 'catalog',
  hidden: (access) => !canListTriggers(access),
  search: async (context) => {
    const data = await fetchList<GetTriggerDefinitionsQuery>(
      context,
      GetTriggerDefinitionsDocument
    );

    return data.triggerDefinitions
      .filter((trigger) => !trigger.archivedAt)
      .filter((trigger) =>
        matchesTerms(
          context.terms,
          trigger.name,
          EVENT_TYPE_LABELS[trigger.eventMatcher.eventType],
          trigger.eventMatcher.caraAddressConfig?.emailSuffix
        )
      )
      .sort(byUpdatedAtDesc)
      .map((trigger) => ({
        id: trigger.id,
        label: trigger.name,
        description: [
          EVENT_TYPE_LABELS[trigger.eventMatcher.eventType],
          trigger.status === TriggerDefinitionStatus.Paused ? 'Paused' : undefined
        ]
          .filter(Boolean)
          .join(' · '),
        date: relativeDate(trigger.updatedAt),
        primary: {
          label: 'Open',
          target: { kind: 'route', to: `/app/workflows/triggers/${trigger.id}` }
        },
        secondary: {
          label: 'Edit',
          target: { kind: 'route' as const, to: `/app/workflows/triggers/${trigger.id}/edit` }
        }
      }));
  }
};

/** Matches what the Executions tab loads, so the palette reads the list that page cached. */
const TRIGGER_EVENTS_PAGE_SIZE = 50;

export const triggerExecutionsSection: SearchSection = {
  key: 'trigger-executions',
  title: 'Trigger executions',
  icon: QueueListIcon,
  sectionClass: 'content',
  hidden: (access) => !canListTriggers(access),
  search: async (context) => {
    const data = await fetchList<GetTriggerEventsQuery>(context, GetTriggerEventsDocument, {
      first: TRIGGER_EVENTS_PAGE_SIZE
    });

    return data.triggerEvents.edges
      .map(({ node }) => node)
      .map((event) => ({
        event,
        triggerNames: (event.runs ?? []).map((run) => run.triggerDefinition.name)
      }))
      .filter(({ event, triggerNames }) =>
        matchesTerms(
          context.terms,
          eventSummary(event),
          EVENT_TYPE_LABELS[event.eventType],
          ...triggerNames
        )
      )
      .sort(
        (a, b) => new Date(b.event.receivedAt).getTime() - new Date(a.event.receivedAt).getTime()
      )
      .map(({ event, triggerNames }) => ({
        id: event.id,
        label: eventSummary(event),
        description: triggerNames.join(', ') || EVENT_TYPE_LABELS[event.eventType],
        date: relativeDate(event.receivedAt),
        primary: {
          label: 'Open',
          target: { kind: 'route', to: `/app/workflows/events/${event.id}` }
        }
      }));
  }
};

/**
 * The product docs at docs.getcara.ai, searched through their own index rather
 * than a list kept here: searchDocumentation proxies the site's search, so
 * every page is reachable and none of it needs restating in this repo.
 *
 * Off-platform, and the slowest section for it -- the resolver makes a
 * third-party call, so this is the one most often still resolving when the
 * rest of the palette has settled.
 */
/**
 * Docs snippets arrive as markdown -- headings, links, code fences, list
 * markers. The row renders one truncated line, so everything is stripped and
 * the whitespace the block structure leaves behind collapses to single spaces.
 *
 * No `keep`, unlike the workspace copy-to-clipboard helpers: list markers earn
 * their place in text someone is about to paste, not in a subtitle.
 */
const plainTextSnippet = async (markdown: string) => {
  const stripped = await remark().use(stripMarkdown).process(markdown);
  return stripped.toString().replace(/\s+/g, ' ').trim();
};

export const documentationSection: SearchSection = {
  key: 'documentation',
  title: 'Documentation',
  icon: BookOpenIcon,
  sectionClass: 'content',
  search: async (context) => {
    const data = await fetchList<SearchDocumentationQuery>(context, SearchDocumentationDocument, {
      query: context.query
    });

    // No client-side filter: the docs index has already decided what matches,
    // and re-running matchesTerms over a snippet would drop pages whose match
    // was in body text the snippet does not happen to quote.
    return Promise.all(
      data.searchDocumentation.results.map(async (result) => ({
        id: result.path,
        label: result.title,
        description: (await plainTextSnippet(result.content)) || undefined,
        primary: {
          label: 'Read' as const,
          target: { kind: 'external' as const, url: result.path }
        }
      }))
    );
  }
};
